import React, { useMemo } from "react";
import Terminal from "./Terminal";
import TerminalLine from "./TerminalLine";
import "./TerminalQuestion.css";

/**
 * Componente para renderizar una pregunta tipo terminal con dropdowns integrados
 * @param {Object} props
 * @param {Object} props.pregunta - Objeto pregunta con contenido_terminal
 * @param {Object} props.opcionesPorDropdown - Objeto que agrupa opciones por dropdown_id
 * @param {Object} props.respuestas - Objeto con las respuestas seleccionadas
 * @param {Function} props.onSelectDropdown - Callback cuando se selecciona una opción
 * @param {boolean} props.disabled - Si el componente está deshabilitado
 */
export const TerminalQuestion = ({
  pregunta,
  opcionesPorDropdown,
  respuestas,
  onSelectDropdown,
  disabled = false,
  showCorrectAnswer = false,
  respuestaCorrecta = null,
}) => {
  // Parsear el contenido de la terminal
  const lineas = useMemo(() => {
    if (!pregunta.contenido_terminal || !pregunta.contenido_terminal.lineas) {
      return [];
    }
    return pregunta.contenido_terminal.lineas;
  }, [pregunta.contenido_terminal]);

  // Obtener respuesta actual para esta pregunta
  const respuestaActual = respuestas[pregunta.id] || {};

  // Función auxiliar para renderizar un dropdown
  const renderDropdown = (dropdownId, key) => {
    const opciones = opcionesPorDropdown[dropdownId] || [];

    // Obtener la opción seleccionada para este dropdown
    const opcionSeleccionada =
      typeof respuestaActual === "object" &&
      respuestaActual !== null &&
      !Array.isArray(respuestaActual) &&
      typeof respuestaActual !== "number"
        ? respuestaActual[`dropdown_${dropdownId}`] || null
        : null;

    // Obtener respuesta correcta si está disponible
    const correctaId = showCorrectAnswer && respuestaCorrecta ? respuestaCorrecta[`dropdown_${dropdownId}`] : null;
    const opcionCorrecta = correctaId != null ? opciones.find((o) => o.id === correctaId) : null;
    const esCorrecto = correctaId != null && opcionSeleccionada === correctaId;
    const esIncorrecto = showCorrectAnswer && correctaId != null && opcionSeleccionada != null && opcionSeleccionada !== correctaId;
    const dropdownFeedbackClass = showCorrectAnswer && correctaId != null
      ? esCorrecto
        ? ' terminal-dropdown-correct'
        : esIncorrecto
        ? ' terminal-dropdown-incorrect'
        : ' terminal-dropdown-sin-responder'
      : '';

    return (
      <span key={key} className="terminal-dropdown-wrapper">
        <select
          className={`terminal-dropdown${dropdownFeedbackClass}`}
          value={opcionSeleccionada || ""}
          onChange={(e) => {
            if (!showCorrectAnswer) {
              const selectedValue = e.target.value;
              if (selectedValue && !disabled) {
                const selectedOpcionId = parseInt(selectedValue);
                if (!isNaN(selectedOpcionId)) {
                  onSelectDropdown(pregunta.id, dropdownId, selectedOpcionId);
                }
              }
            }
          }}
          disabled={disabled || showCorrectAnswer}
        >
          <option value="">-- Select --</option>
          {opciones
            .sort((a, b) => (a.orden || 0) - (b.orden || 0))
            .map((opcion) => (
              <option key={opcion.id} value={opcion.id}>
                {opcion.texto}
              </option>
            ))}
        </select>
        {showCorrectAnswer && opcionCorrecta && (
          <div className="terminal-dropdown-feedback">
            {esCorrecto && <span className="terminal-feedback-check">✓</span>}
            {esIncorrecto && <span className="terminal-feedback-cross">✗</span>}
            <span className="terminal-correct-answer-text">
              Correct: <strong>{opcionCorrecta.texto}</strong>
            </span>
          </div>
        )}
      </span>
    );
  };

  const renderLinea = (linea, index) => {
    // Línea mixta: contiene múltiples elementos (texto y dropdowns) en la misma línea
    if (linea.tipo === "mixta" && linea.elementos) {
      return (
        <TerminalLine key={`mixta-${index}`} className="terminal-line-mixta">
          {linea.elementos.map((elemento, elemIndex) => {
            if (elemento.tipo === "texto") {
              return (
                <span key={`texto-${elemIndex}`} className="terminal-text">
                  {elemento.valor || ""}
                </span>
              );
            } else if (elemento.tipo === "dropdown") {
              return renderDropdown(
                elemento.dropdown_id,
                `dropdown-${elemento.dropdown_id}-${elemIndex}`
              );
            }
            return null;
          })}
        </TerminalLine>
      );
    }

    // Dropdown completo (línea separada) - compatibilidad hacia atrás
    if (linea.tipo === "dropdown") {
      const dropdownId = linea.dropdown_id;
      return (
        <TerminalLine key={`dropdown-${dropdownId}-${index}`}>
          {renderDropdown(dropdownId, `dropdown-${dropdownId}`)}
        </TerminalLine>
      );
    }

    // Texto completo (línea separada) - compatibilidad hacia atrás
    if (linea.tipo === "texto") {
      return (
        <TerminalLine key={`texto-${index}`}>
          <span className="terminal-text">{linea.valor || ""}</span>
        </TerminalLine>
      );
    }

    return null;
  };

  return (
    <div className="terminal-answer-area-container">
      <div className="terminal-answer-area-header">
        <h3 className="answer-area-title">Answer Area</h3>
      </div>
      <div className="terminal-question-container">
        <Terminal>
          {lineas.map((linea, index) => renderLinea(linea, index))}
        </Terminal>
      </div>
    </div>
  );
};

export default TerminalQuestion;
